import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';

export interface WeatherProviderConfig {
  primary: string;
  fallbacks?: string[];
  fallback?: string;
}

export const useFeatureFlags = () => {
  const flags = useFlags() ?? {};
  const ldClient = useLDClient();

  const getTheme = (): string => flags['default-theme'] ?? 'dark-synth';
  const getTemperatureUnit = (): string => flags['default-temperature'] ?? 'c';
  const getDistanceUnit = (): string => flags['default-distance'] ?? 'm';
  const getWeatherRefreshInterval = (): number => flags['weather-refresh-interval'] ?? 5;
  const getAnimationsEnabled = (): boolean => flags['enable-animations'] ?? true;
  const getShowExtraWeatherInfo = (): boolean => flags['show-extra-weather-info'] ?? true;
  const getDebugMode = (): boolean => flags['debug-mode'] ?? false;
  const getShowMoonPhase = (): boolean => flags['show-moon-phase'] ?? true;
  const getEnableMaximizedLayout = (): boolean => flags['enable-maximized-layout'] ?? false;
  const getWeatherApiProvider = (): WeatherProviderConfig => {
    const raw = flags['weather-api-provider'];
    if (raw && typeof raw === 'object') return raw as WeatherProviderConfig;
    try {
      return typeof raw === 'string' ? JSON.parse(raw) : { primary: 'openweathermap', fallback: 'open-meteo' };
    } catch {
      return { primary: 'openweathermap', fallback: 'open-meteo' };
    }
  };

  // Generic getter kept for any ad-hoc flag reads
  const getFlag = (flagKey: string, fallbackValue: unknown = null): unknown =>
    flags[flagKey] !== undefined ? flags[flagKey] : fallbackValue;

  return {
    getFlag,
    getTheme,
    getTemperatureUnit,
    getDistanceUnit,
    getWeatherRefreshInterval,
    getAnimationsEnabled,
    getShowExtraWeatherInfo,
    getDebugMode,
    getShowMoonPhase,
    getEnableMaximizedLayout,
    getWeatherApiProvider,
    isLDConnected: !!ldClient,
    ldClient,
  };
};

export default useFeatureFlags;
